// useBuilderInput - 新しい Builder 入力システム
// useScene.ts と同じ機能を提供、src/engine ベース

import { useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { nanoid } from "nanoid";
import { Mesh } from "@babylonjs/core/Meshes";
import { Scene } from "@babylonjs/core/scene";
import { Observer } from "@babylonjs/core/Misc/observable";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Button, AdvancedDynamicTexture } from "@babylonjs/gui";
import { PickingInfo } from "@babylonjs/core/Collisions/pickingInfo";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { ArcRotateCamera } from "@babylonjs/core/Cameras/arcRotateCamera";
import { PointerInfo, PointerEventTypes } from "@babylonjs/core/Events/pointerEvents";
import { RootState } from "app/store";
import Constants from "common/constant";
import { usePlaySound } from "common/utils";
import { AssetModelType } from "common/model";
import { actions } from "features/builder/slice";
import { useActionCommand } from "features/builder/hook";
import { ActionCommandType } from "features/builder/types";
import { createPreviewMesh } from "features/builder/canvas/scene/loader";
import {
  createGround,
  createOuterNode,
  createBottomGround,
} from "features/builder/canvas/scene/util";

export const useBuilderInput = (scene: Scene | null) => {
  const play = usePlaySound();
  const dispatch = useDispatch();
  const { handleAction } = useActionCommand();
  const actionMenu = useSelector((state: RootState) => state.builder.actionMenu);
  const selectedAssetModel = useSelector((state: RootState) => state.builder.selectedAssetModel);

  // 回転ターゲット（選択中のアセット）
  const [target, _setTarget] = useState<Mesh | null>(null);
  const targetRef = useRef(target);
  const setTarget = (t: Mesh | null) => {
    targetRef.current = t;
    _setTarget(t);
    // Redux にも同期
    dispatch(actions.selectPlacedModel(t?.id ?? null));
  };

  // ターゲットクリア（3Dキャンバスをクリックしたとき）
  useEffect(() => {
    if (!scene) return;
    const canvas = scene.getEngine().getRenderingCanvas();
    if (!canvas) return;

    const handleClearTarget = () => {
      if (target) {
        target.getChildMeshes().forEach((subMesh) => {
          if (subMesh.material) subMesh.material.alpha = 1;
        });
        setTarget(null);
      }
    };
    canvas.addEventListener("pointerdown", handleClearTarget);
    return () => {
      canvas.removeEventListener("pointerdown", handleClearTarget);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, scene]);

  // 回転ボタン表示
  useEffect(() => {
    let rotateBtn: Button;
    if (target) {
      const turnMesh = () => {
        const lastForward = target.forward;
        target.addRotation(0, Math.PI / 2, 0);
        dispatch(
          actions.updateMapModel({
            id: target.id,
            changes: {
              direction: {
                x: Number(Math.round(lastForward.z)),
                y: 0,
                z: Number(-Math.round(lastForward.x)),
              },
            },
          }),
        );
      };

      const boundingInfo = target.getBoundingInfo();
      const advancedTexture = AdvancedDynamicTexture.CreateFullscreenUI("UI");
      advancedTexture.layer.layerMask = 2;

      rotateBtn = Button.CreateImageOnlyButton(
        "rotateBtn",
        `${Constants.assetHost}/assets/images/icon_reset_rotation.png`,
      );
      rotateBtn.width = "80px";
      rotateBtn.height = "80px";
      rotateBtn.thickness = 0;
      rotateBtn.onPointerDownObservable.add((_, eventState) => {
        eventState.userInfo.event.stopPropagation();
        turnMesh();
      });
      advancedTexture.addControl(rotateBtn);
      rotateBtn.linkWithMesh(target);
      rotateBtn.linkOffsetY = -(boundingInfo.boundingBox.maximumWorld.y + 150);
    }
    return () => {
      if (rotateBtn) {
        rotateBtn.dispose();
      }
    };
  }, [target, actionMenu, dispatch]);

  // メインのポインターイベント処理
  useEffect(() => {
    let clicked = false;
    let pointerUpFlag = false;
    let pointerDownFlag = false;

    let deleteMesh: Mesh | null = null;
    let selectedMesh: Mesh | null = null;
    let previewedMesh: Mesh | null = null;
    let observable: Observer<PointerInfo> | null = null;

    if (scene) {
      const camera = scene.getCameraByName("camera") as ArcRotateCamera;

      // ポインターダウン処理
      const pointerDown = async (pickInfo: PickingInfo) => {
        if (pointerDownFlag || pointerUpFlag) return;
        clicked = true;
        pointerDownFlag = true;

        if (pickInfo.pickedMesh) {
          if (selectedAssetModel) {
            // ADD モード：アセット配置
            camera?.detachControl();

            const hitAssetMesh = scene.pick(scene.pointerX, scene.pointerY, (mesh) =>
              mesh.name.includes("asset"),
            );
            const hitGroundMesh = scene.pick(scene.pointerX, scene.pointerY, (mesh) =>
              mesh.name.includes("ground"),
            );

            if (
              !previewedMesh &&
              hitAssetMesh.hit &&
              hitGroundMesh.hit &&
              hitGroundMesh.pickedMesh?.name.split("_").pop() ===
                hitAssetMesh.pickedMesh?.name.split("_").pop() &&
              (selectedAssetModel.type !== AssetModelType.RIVER &&
              selectedAssetModel.type !== AssetModelType.GROUND
                ? hitGroundMesh.pickedMesh?.name.split("_").pop() !== "0"
                : true)
            ) {
              scene.getMaterialByName("ground_layer")!.alpha = 0.3;
              if (
                selectedAssetModel.type === AssetModelType.RIVER ||
                selectedAssetModel.type === AssetModelType.GROUND
              ) {
                scene.getMaterialByName("ground")!.alpha = 0.3;
              }

              const mesh = await createPreviewMesh(
                scene,
                selectedAssetModel.filename,
                selectedAssetModel.offset,
              );
              if (clicked) {
                mesh.getChildMeshes().forEach((subMesh) => {
                  if (subMesh.material) subMesh.material.alpha = 0.8;
                  if (subMesh.name.indexOf("Cube") !== -1) {
                    subMesh.isVisible = false;
                  }
                });
                const outer = createOuterNode(scene, hitGroundMesh.pickedMesh!.position);
                if (selectedAssetModel.type === AssetModelType.CHARACTER) {
                  outer.scaling = new Vector3(
                    selectedAssetModel.scaling.x,
                    selectedAssetModel.scaling.y,
                    selectedAssetModel.scaling.z,
                  );
                  scene.animationGroups.forEach((group) => {
                    group.stop();
                  });
                }
                mesh.metadata = {
                  type: selectedAssetModel.type,
                  rotation: selectedAssetModel.rotation,
                };
                mesh.parent = outer;
                previewedMesh = mesh;
              } else {
                mesh.dispose();
                previewedMesh = null;
                scene.getMaterialByName("ground")!.alpha = 0;
                scene.getMaterialByName("ground_layer")!.alpha = 0;
              }
            }
          } else {
            // SELECT/ERASE モード
            if (!actionMenu.eraser) {
              dispatch(
                actions.updateActionMenu({
                  key: "tap",
                  value: true,
                  exclusive: true,
                }),
              );
            }

            // ground以外のすべてのアセットメッシュを選択可能に
            const hitAssetMesh = scene.pick(scene.pointerX, scene.pointerY, (mesh) => {
              // outer の子要素であればアセットメッシュ
              let parent = mesh.parent;
              while (parent) {
                if (parent.name === "outer") return true;
                parent = parent.parent;
              }
              return false;
            });
            if (hitAssetMesh.pickedMesh) {
              let hitMesh = hitAssetMesh.pickedMesh as Mesh;
              while (hitMesh.parent !== null && hitMesh.parent.name !== "outer") {
                hitMesh = hitMesh.parent as Mesh;
              }
              const ground = scene.getMeshByName(hitMesh.name.replace("model", "ground"));
              if (hitMesh.metadata?.type !== "ground" || ground) {
                camera?.detachControl();
                if (targetRef.current) {
                  targetRef.current.getChildMeshes().forEach((subMesh) => {
                    if (subMesh.material) subMesh.material.alpha = 1;
                  });
                  setTarget(null);
                }
                hitMesh.getChildMeshes().forEach((subMesh) => {
                  if (subMesh.material) subMesh.material.alpha = 0.8;
                });
                if (actionMenu.eraser) {
                  deleteMesh = hitMesh;
                } else {
                  // 移動モード
                  scene.getMaterialByName("ground_layer")!.alpha = 0.3;
                  if (
                    hitMesh.metadata?.type === AssetModelType.RIVER ||
                    hitMesh.metadata?.type === AssetModelType.GROUND
                  ) {
                    scene.getMaterialByName("ground")!.alpha = 0.3;
                  }

                  const outer = hitMesh.parent as TransformNode;
                  const boundingInfo = hitMesh.getBoundingInfo();
                  createBottomGround(scene, boundingInfo.boundingBox, outer.position);
                  selectedMesh = hitMesh;
                }
              }
            }
          }
        } else {
          // 何もない場所をクリック
          play();
          dispatch(
            actions.updateActionMenu({
              key: "tap",
              value: true,
              exclusive: true,
            }),
          );
          dispatch(actions.selectedAssetMesh(null));
        }

        pointerDownFlag = false;
      };

      // ポインター移動処理
      const pointerMove = async () => {
        if (!clicked || pointerDownFlag || pointerUpFlag) return;
        if ((selectedAssetModel && previewedMesh) || selectedMesh) {
          const hitGroundMesh = scene.pick(scene.pointerX, scene.pointerY, (mesh) =>
            mesh.name.includes("ground"),
          );

          if (hitGroundMesh.pickedPoint) {
            if (
              previewedMesh &&
              (selectedAssetModel?.type !== AssetModelType.RIVER &&
              selectedAssetModel?.type !== AssetModelType.GROUND
                ? hitGroundMesh.pickedMesh?.name.split("_").pop() !== "0"
                : true)
            ) {
              (previewedMesh.parent as TransformNode).position = hitGroundMesh.pickedMesh!.position;
            }
            if (selectedMesh) {
              (selectedMesh.parent as TransformNode).position = hitGroundMesh.pickedMesh!.position;
            }
          }
        }
      };

      // ポインターアップ処理
      const pointerUp = async (pickInfo: PickingInfo) => {
        clicked = false;

        if (pointerDownFlag || pointerUpFlag) {
          if (previewedMesh) {
            (previewedMesh.parent as TransformNode).dispose();
            previewedMesh = null;
          }
          return;
        }

        pointerUpFlag = true;
        scene.getMaterialByName("ground")!.alpha = 0;
        scene.getMaterialByName("ground_layer")!.alpha = 0;

        if (selectedAssetModel && pickInfo.pickedMesh && previewedMesh) {
          // 新しいアセットを配置
          play();
          const animationGroups = [...scene.animationGroups];
          animationGroups.forEach((group) => {
            group.stop();
            group.dispose();
          });
          scene.animationGroups.length = 0;

          const hitGroundMesh = scene.pick(scene.pointerX, scene.pointerY, (mesh) =>
            mesh.name.includes("ground"),
          );
          const outer = previewedMesh.parent as TransformNode;
          if (
            hitGroundMesh.hit &&
            hitGroundMesh.pickedMesh!.position.x === outer.position.x &&
            hitGroundMesh.pickedMesh!.position.z === outer.position.z &&
            (selectedAssetModel.type !== AssetModelType.RIVER &&
            selectedAssetModel.type !== AssetModelType.GROUND
              ? hitGroundMesh.pickedMesh?.name.split("_").pop() !== "0"
              : true)
          ) {
            const meshId = nanoid();
            const boundingInfo = previewedMesh.getBoundingInfo();
            const name = `asset_model_layer_${hitGroundMesh.pickedMesh!.position.x}_${hitGroundMesh.pickedMesh!.position.z}_${Math.round(boundingInfo.boundingBox.maximumWorld.y)}`;
            outer.getChildMeshes().forEach((subMesh) => {
              if (subMesh.material) {
                subMesh.material.alpha = 1;
              }
              if (subMesh.name !== "outer") {
                subMesh.id = meshId;
                subMesh.name = name;
              }
            });
            previewedMesh.metadata["position"] = {
              x: hitGroundMesh.pickedMesh!.position.x,
              y: hitGroundMesh.pickedMesh!.position.y,
              z: hitGroundMesh.pickedMesh!.position.z,
            };
            previewedMesh.metadata["bottomLayer"] = hitGroundMesh.pickedMesh?.name.split("_").pop();
            createGround(
              scene,
              boundingInfo.boundingBox,
              selectedAssetModel.type,
              hitGroundMesh.pickedMesh!.position,
              hitGroundMesh.pickedMesh?.name.split("_").pop() ?? "0",
            );
            handleAction({
              type: ActionCommandType.ADD_MODE,
              model: {
                id: meshId,
                type: selectedAssetModel.type,
                name,
                filename: selectedAssetModel.filename,
                rotation: selectedAssetModel.rotation,
                offset: selectedAssetModel.offset,
                scaling: selectedAssetModel.scaling,
                metadata: selectedAssetModel.metadata,
                position: {
                  x: hitGroundMesh.pickedMesh!.position.x,
                  y: hitGroundMesh.pickedMesh!.position.y,
                  z: hitGroundMesh.pickedMesh!.position.z,
                },
              },
            });

            previewedMesh = null;
          } else {
            if (previewedMesh) {
              (previewedMesh.parent as TransformNode).dispose();
              previewedMesh = null;
            }
          }

          dispatch(actions.resetActionMenu());
          camera?.attachControl(scene.getEngine().getRenderingCanvas()!, true);
        } else if (selectedMesh) {
          // 既存アセットを移動
          const hitGroundMesh = scene.pick(scene.pointerX, scene.pointerY, (mesh) =>
            mesh.name.includes("ground"),
          );
          const outer = selectedMesh.parent as TransformNode;
          const boundingInfo = selectedMesh.getBoundingInfo();
          if (
            hitGroundMesh.hit &&
            hitGroundMesh.pickedMesh!.position.x === outer.position.x &&
            hitGroundMesh.pickedMesh!.position.z === outer.position.z &&
            (selectedMesh.metadata.type !== AssetModelType.RIVER &&
            selectedMesh.metadata.type !== AssetModelType.GROUND
              ? hitGroundMesh.pickedMesh?.name.split("_").pop() !== "0"
              : true)
          ) {
            const name = `asset_model_layer_${hitGroundMesh.pickedMesh!.position.x}_${hitGroundMesh.pickedMesh!.position.z}_${Math.round(boundingInfo.boundingBox.maximumWorld.y)}`;
            outer.getChildMeshes().forEach((subMesh) => {
              if (subMesh.material && !selectedMesh!.metadata?.rotation) {
                subMesh.material.alpha = 1;
              }
              if (subMesh.name !== "outer") {
                subMesh.name = name;
              }
            });
            selectedMesh.metadata["position"] = {
              x: hitGroundMesh.pickedMesh!.position.x,
              y: hitGroundMesh.pickedMesh!.position.y,
              z: hitGroundMesh.pickedMesh!.position.z,
            };
            selectedMesh.metadata["bottomLayer"] = hitGroundMesh.pickedMesh?.name.split("_").pop();
            createGround(
              scene,
              boundingInfo.boundingBox,
              selectedMesh.metadata.type,
              hitGroundMesh.pickedMesh!.position,
              hitGroundMesh.pickedMesh?.name.split("_").pop() ?? "0",
            );

            handleAction({
              type: ActionCommandType.CHANGE_MODE,
              id: selectedMesh.id,
              changes: {
                name,
                position: {
                  x: hitGroundMesh.pickedMesh!.position.x,
                  y: hitGroundMesh.pickedMesh!.position.y,
                  z: hitGroundMesh.pickedMesh!.position.z,
                },
              },
            });
          } else {
            const position = new Vector3(
              selectedMesh.metadata.position.x,
              selectedMesh.metadata.position.y,
              selectedMesh.metadata.position.z,
            );
            outer.position = position;
            outer.getChildMeshes().forEach((subMesh) => {
              if (subMesh.material && !selectedMesh!.metadata?.rotation) {
                subMesh.material.alpha = 1;
              }
            });
            createGround(
              scene,
              boundingInfo.boundingBox,
              selectedMesh.metadata.type,
              position,
              selectedMesh.metadata.bottomLayer,
            );
          }

          // すべての配置済みアセットを選択可能に
          setTarget(selectedMesh);

          if (!selectedMesh.metadata?.rotation) {
            camera?.attachControl(scene.getEngine().getRenderingCanvas()!, true);
          }

          selectedMesh = null;
        } else if (deleteMesh) {
          if (actionMenu.eraser) {
            // アセットを削除
            play();
            const ground = scene.getMeshByName(deleteMesh.name.replace("model", "ground"));
            if (deleteMesh.metadata?.type !== "ground" || ground) {
              const outer = deleteMesh.parent as TransformNode;
              const boundingInfo = deleteMesh.getBoundingInfo();
              createBottomGround(scene, boundingInfo.boundingBox, outer.position);
              handleAction({
                type: ActionCommandType.REMOVE_MODE,
                meshId: deleteMesh.id,
              });
              outer.dispose();
            }
          }
          deleteMesh = null;
          camera?.attachControl(scene.getEngine().getRenderingCanvas()!, true);
        } else {
          if (previewedMesh) {
            (previewedMesh.parent as TransformNode).dispose();
            previewedMesh = null;
          }
          camera?.attachControl(scene.getEngine().getRenderingCanvas()!, true);
        }

        pointerUpFlag = false;
      };

      // ポインターオブザーバー登録
      observable = scene.onPointerObservable.add(async (pointerInfo) => {
        switch (pointerInfo.type) {
          case PointerEventTypes.POINTERDOWN:
            await pointerDown(pointerInfo.pickInfo!);
            break;
          case PointerEventTypes.POINTERMOVE:
            await pointerMove();
            break;
          case PointerEventTypes.POINTERUP:
            await pointerUp(pointerInfo.pickInfo!);
            break;
        }
      });
    }

    return () => {
      clicked = false;
      pointerUpFlag = false;
      pointerDownFlag = false;
      selectedMesh = null;
      previewedMesh = null;
      if (observable && scene) {
        scene.onPointerObservable.remove(observable);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene, actionMenu.eraser, selectedAssetModel, dispatch, handleAction, play]);
};
